/**
 * Profile Form Validation Schemas
 * Zod schemas for personal and professional profile forms
 */

import { z } from 'zod'

// ============================================================================
// Personal Profile Schema
// ============================================================================

export const personalProfileSchema = z.object({
  firstName: z
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre no puede exceder 50 caracteres')
    .regex(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, 'El nombre solo puede contener letras'),

  lastName: z
    .string()
    .min(2, 'El apellido debe tener al menos 2 caracteres')
    .max(50, 'El apellido no puede exceder 50 caracteres')
    .regex(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, 'El apellido solo puede contener letras'),

  email: z
    .string()
    .email('Ingresa un correo electrónico válido')
    .optional()
    .or(z.literal('')),

  phone: z
    .string()
    .min(10, 'El teléfono debe tener al menos 10 dígitos')
    .regex(/^[0-9\s\-+()]+$/, 'Formato de teléfono inválido'),

  cityId: z
    .string()
    .optional(),

  // Professional license fields
  professionalType: z
    .enum(['Arq', 'Ing'])
    .optional()
    .or(z.literal('')),

  licenseNumber: z
    .string()
    .optional()
    .or(z.literal('')),
})

export type PersonalProfileFormData = z.infer<typeof personalProfileSchema>

// ============================================================================
// Professional Profile Schema
// ============================================================================

export const professionalProfileSchema = z.object({
  professionalName: z
    .string()
    .min(2, 'El nombre profesional debe tener al menos 2 caracteres')
    .max(100, 'El nombre profesional no puede exceder 100 caracteres'),

  bio: z
    .string()
    .max(500, 'La descripción no puede exceder 500 caracteres')
    .optional()
    .or(z.literal('')),

  professionId: z
    .string()
    .min(1, 'Debes seleccionar una profesión'),

  cityId: z
    .string()
    .min(1, 'Debes seleccionar una ciudad'),

  contactPhone: z
    .string()
    .min(10, 'El teléfono debe tener al menos 10 dígitos')
    .regex(/^[0-9\s\-+()]+$/, 'Formato de teléfono inválido'),

  profilePictureUrl: z
    .string()
    .optional()
    .or(z.literal('')),

  instagramUrl: z
    .string()
    .max(200, 'El usuario de Instagram no puede exceder 200 caracteres')
    .optional()
    .or(z.literal('')),

  facebookUrl: z
    .string()
    .max(200, 'El usuario de Facebook no puede exceder 200 caracteres')
    .optional()
    .or(z.literal('')),

  googleMapsUrl: z
    .string()
    .max(500, 'La dirección de Google Maps no puede exceder 500 caracteres')
    .optional()
    .or(z.literal('')),

  // Professional license fields
  hasLicense: z
    .boolean()
    .optional(),

  licenseNumber: z
    .string()
    .optional()
    .or(z.literal('')),
})

export type ProfessionalProfileFormData = z.infer<typeof professionalProfileSchema>
